/**
 * AdminFinancialMetrics.tsx
 * 
 * Reusable component for displaying financial metrics in admin dashboard.
 * Maintains exact visual design from AdminFinancials page.
 */

import React from 'react'
import { 
  DollarSign, 
  TrendingUp, 
  CreditCard, 
  Users
} from 'lucide-react'

interface FinancialMetricsProps {
  totalRevenue: number
  monthlyRevenue: number
  pendingPayouts: number
  commissionEarned: number
  revenueGrowth: number
}

const AdminFinancialMetrics: React.FC<FinancialMetricsProps> = ({
  totalRevenue,
  monthlyRevenue,
  pendingPayouts,
  commissionEarned,
  revenueGrowth
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(totalRevenue)}
            </p>
          </div>
          <div className={`p-2 rounded-full ${
            revenueGrowth >= 0 ? 'bg-green-100' : 'bg-red-100'
          }`}>
            <TrendingUp className={`h-5 w-5 ${
              revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'
            }`} />
          </div>
        </div>
        <div className="mt-2">
          <span className={`text-sm ${
            revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {formatPercentage(revenueGrowth)} from last period
          </span>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(monthlyRevenue)}
            </p>
          </div>
          <div className="p-2 rounded-full bg-blue-100">
            <DollarSign className="h-5 w-5 text-blue-600" />
          </div>
        </div>
        <div className="mt-2">
          <span className="text-sm text-gray-500">Current month</span>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Pending Payouts</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(pendingPayouts)}
            </p>
          </div>
          <div className="p-2 rounded-full bg-purple-100">
            <Users className="h-5 w-5 text-purple-600" />
          </div>
        </div>
        <div className="mt-2">
          <span className="text-sm text-gray-500">Awaiting distribution</span>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Commission Earned</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(commissionEarned)}
            </p>
          </div>
          <div className="p-2 rounded-full bg-orange-100">
            <CreditCard className="h-5 w-5 text-orange-600" />
          </div>
        </div>
        <div className="mt-2">
          <span className="text-sm text-green-600">Platform earnings</span>
        </div>
      </div>
    </div>
  )
}

export default AdminFinancialMetrics 